import ApiService, { NoAuthService } from "./ApiService";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./basequery";

const servicePrefix = "/auth";

export const authService = createApi({
  reducerPath: "authApi",
  baseQuery: baseQuery,
  tagTypes: ["apiKeys", "teams"],
  // keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: true,
  endpoints: (build) => ({
    changePassword: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/change_password/`,
        method: "patch",
        data: { ...values },
      }),
    }),

    onVerify: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/verify_otp`,
        method: "post",
        data: { ...values },
      }),
    }),

    resendOtp: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/get_otp`,
        method: "patch",
        data: { ...values },
      }),
    }),
    onSignUp: build.mutation({
      query: ({refCode, ...values }) => ({      
        url: `${servicePrefix}/register/?ref_code=${refCode}`,
        method: "post",
        data: { ...values },
      }),
    }),
    getUser: build.query({
      query: () => ({
        url: `${servicePrefix}/users/`,
        method: "get",
      }),
    }),
    getProfile: build.query({
      query: () => ({
        url: `/profiles/`,
        method: "get",
        data: results,
      }),
    }),

    getApiKeys: build.query({
      query: () => ({
        url: servicePrefix + "/key/",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result?.map?.(({ id }) => ({ type: "apiKeys", id })),
              { type: "apiKeys", id: "LIST" },
            ]
          : [{ type: "apiKeys", id: "LIST" }],
    }),

    createApiKeys: build.mutation({
      query: ({ ...values }) => ({
        url: servicePrefix + "/key/",
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "apiKeys", id: "LIST" }],
    }),

    deleteApiKey: build.mutation({
      query: ({ key }) => ({
        url: servicePrefix + "/key/" + key + "/",
        method: "delete",
      }),
      invalidatesTags: [{ type: "apiKeys", id: "LIST" }],
    }),

    createTeam: build.mutation({
      query: ({ ...values }) => ({
        url: "/teams/create/",
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "teams", id: "LIST" }],
    }),

    inviteTeamMember: build.mutation({
      query: ({ team_id, ...values }) => ({
        url: `/teams/invite/${team_id}/`,
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "teams", id: "LIST" }],
    }),

    cancelTeamInvite: build.mutation({
      query: ({ team_id, ...values }) => ({
        url: `/teams/invite/cancel/${team_id}/`,
        method: "delete",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "teams", id: "LIST" }],
    }),

    retryInvitation: build.mutation({
      query: ({ permission_id, ...values }) => ({
        url: `/teams/invite/retry/${permission_id}`,
        method: "post",
        data: { ...values },
      }),
    }),

    getTeams: build.query({
      query: () => ({
        url: "/teams/",
      }),
    }),

    getTeamInvites: build.query({
      query: ({ team_id }) => ({
        url: `/teams/invite/list/${team_id}`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result?.data?.map?.(({ id }) => ({ type: "teams", id })),
              { type: "teams", id: "LIST" },
            ]
          : [{ type: "teams", id: "LIST" }],
    }),
    getReferralCode: build.query({
      query: () => ({
        url: `/referrals/code/`,
        method: "get",
      }),
    }),
    getReferral: build.query({
      query: () => ({
        url: `/referrals/`,
        method: "get",
      }),
    }),
  }),
});

export const {
  useChangePasswordMutation,
  useOnVerifyMutation,
  useOnSignUpMutation,
  useResendOtpMutation,
  // api keys
  useCreateApiKeysMutation,
  useDeleteApiKeyMutation,
  useGetApiKeysQuery,
  useGetProfileQuery,
  useGetUserQuery,
  useCreateTeamMutation,
  useGetTeamsQuery,
  useInviteTeamMemberMutation,
  useGetTeamInvitesQuery,
  useGetReferralCodeQuery,
  useGetReferralQuery,
  useCancelTeamInviteMutation,
  useRetryInvitationMutation,
} = authService;

export async function apiSignIn(data) {
  return NoAuthService.fetchData({
    // url: "/sign-in",
    url: servicePrefix + "/login/",
    method: "post",
    data,
  });
}

export async function apiSignInGoogle(data) {
  return NoAuthService.fetchData({
    url: "/socials/google/",
    method: "post",
    data,
  });
}

export async function apiSignInGithub(data) {
  return NoAuthService.fetchData({
    url: "/socials/github/",
    method: "post",
    data,
  });
}
export async function apiVerify(data) {
  return NoAuthService.fetchData({
    // url: "/sign-in",
    url: servicePrefix + "/verify_otp/",
    method: "post",
    data,
  });
}
export async function apiResendOtp(data) {
  return NoAuthService.fetchData({
    // url: "/sign-in",
    url: servicePrefix + "/get_otp/",
    method: "patch",
    data,
  });
}

export async function apiSignUp({refCode, formData}) {
  const url = refCode ? servicePrefix + `/register/?ref_code=${refCode}` : servicePrefix + `/register/`;

  return NoAuthService.fetchData({
    url,
    method: "post",
    data:formData,
  });
}

export async function apiInviteSignUp(data) {
  return NoAuthService.fetchData({
    url: "/teams/invite/process/",
    method: "post",
    data,
  });
}

export async function apiSignOut(data) {
  return ApiService.fetchData({
    // url: "/sign-out",
    url: servicePrefix + "/logout/",
    method: "post",
    data,
  });
}

export async function apiForgotPassword(data) {
  return NoAuthService.fetchData({
    // url: "/forgot-password",
    url: servicePrefix + "/password_reset_request_email/",
    method: "post",
    data,
  });
}

export async function apiResetPassword(data) {
  return NoAuthService.fetchData({
    // url: "/reset-password",
    url: servicePrefix + "/password_reset_complete/",
    method: "patch",
    data,
  });
}

export async function apiGetUser() {
  const { data } = await ApiService.fetchData({
    url: servicePrefix + "/users/",
    method: "get",
  });
  return data.data;
}
export async function apiGetProfile() {
  const { data } = await ApiService.fetchData({
    url: "/profiles/",
    method: "get",
  });
  return data;
}

export async function apiGetPublicProfileLink() {
  const { data } = await ApiService.fetchData({
    url: "/profiles/profile-link/",
    method: "get",
  });
  return data;
}

export async function apiUpdateUser(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/users/",
    method: "patch",
    data,
  });
}
export async function apiUpdateProfile(data) {
  return ApiService.fetchData({
    url: "/profiles/",
    method: "patch",
    data,
  });
}

export async function apiChangePassword(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/change_password/",
    method: "patch",
    data,
  });
}

export async function apiReferralsCode() {

 const data = await ApiService.fetchData({
    url:  "/referrals/code/",
    method: "get",
  });
  return data
}
export async function apiGetKeys() {
  return ApiService.fetchData({
    url: servicePrefix + "/key/",
    method: "get",
  });
}
export async function apiCreateKey(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/key/",
    method: "post",
    data,
  });
}
export async function apiDeleteKey(key) {
  return ApiService.fetchData({
    url: servicePrefix + "/key/" + key + "/",
    method: "delete",
  });
}
